import type { ManagedCache, CacheOptions, CacheStats } from '@gzl10/nexus-sdk'
import type { Redis } from 'ioredis'

/**
 * Redis-backed implementation of ManagedCache<T>.
 *
 * Key schema: ${prefix}:cache:${cacheName}:${key}
 * The `cache:` namespace prevents collision with RedisAdapter (entity storage).
 *
 * Size tracking: Uses a local counter (trackedSize) instead of querying Redis
 * on every getSize() call. May drift from Redis reality if keys expire via TTL.
 */
export class RedisManagedCache<T = unknown> implements ManagedCache<T> {
  private readonly keyPrefix: string
  private hits = 0
  private misses = 0
  private trackedSize = 0
  private maxEntries: number
  private defaultTTL?: number
  private onAddRules?: (events: string[]) => void

  constructor(
    public readonly name: string,
    private readonly client: Redis,
    prefix: string,
    options?: CacheOptions,
    onAddRules?: (events: string[]) => void
  ) {
    this.keyPrefix = `${prefix}:cache:${name}`
    this.maxEntries = options?.maxEntries ?? 100
    this.defaultTTL = options?.defaultTTL
    this.onAddRules = onAddRules
  }

  async get(key: string): Promise<T | null> {
    const raw = await this.client.get(this.redisKey(key))
    if (raw === null) {
      this.misses++
      return null
    }
    this.hits++
    return JSON.parse(raw) as T
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    const serialized = JSON.stringify(value)
    const effectiveTTL = ttl ?? this.defaultTTL

    const existed = await this.client.exists(this.redisKey(key))

    if (effectiveTTL) {
      await this.client.setex(this.redisKey(key), effectiveTTL, serialized)
    } else {
      await this.client.set(this.redisKey(key), serialized)
    }

    if (!existed) {
      this.trackedSize++
    }
  }

  async delete(key: string): Promise<boolean> {
    const deleted = await this.client.del(this.redisKey(key))
    if (deleted > 0) {
      this.trackedSize = Math.max(0, this.trackedSize - 1)
      return true
    }
    return false
  }

  async deleteByPrefix(prefix: string): Promise<number> {
    const pattern = `${this.keyPrefix}:${prefix}*`
    let cursor = '0'
    let deleted = 0
    do {
      const [next, keys] = await this.client.scan(cursor, 'MATCH', pattern, 'COUNT', 100)
      cursor = next
      if (keys.length > 0) {
        await this.client.del(...keys)
        deleted += keys.length
      }
    } while (cursor !== '0')
    this.trackedSize = Math.max(0, this.trackedSize - deleted)
    return deleted
  }

  async has(key: string): Promise<boolean> {
    return (await this.client.exists(this.redisKey(key))) === 1
  }

  async clear(): Promise<void> {
    await this.deleteByPrefix('')
    this.trackedSize = 0
  }

  async prune(): Promise<number> {
    return 0
  }

  async getSize(): Promise<number> {
    return this.trackedSize
  }

  getStats(): CacheStats {
    const total = this.hits + this.misses
    return {
      hits: this.hits,
      misses: this.misses,
      hitRate: total > 0 ? this.hits / total : 0,
      size: this.trackedSize,
      maxEntries: this.maxEntries
    }
  }

  resetStats(): void {
    this.hits = 0
    this.misses = 0
  }

  addInvalidationRules(events: string[]): void {
    this.onAddRules?.(events)
  }

  private redisKey(key: string): string {
    return `${this.keyPrefix}:${key}`
  }
}
